// A chat's thread and composer (D82), given the decrypted conversation and its turns: what came
// before the chat (a task's workflow steps), then each turn with its prompt, progress and
// replies, then prompts still on their way. Testable without a relay: the page passes `send`.
import { ChatsCircle } from "@phosphor-icons/react";
import { useEffect, useRef } from "react";

import type { ConversationState } from "../conversation/model";
import { EventRow } from "../conversation/EventRow";
import { foldEvents } from "../conversation/thinking";
import { ThinkingRow } from "../conversation/ThinkingRow";
import type { ConversationHandle } from "../conversation/useConversation";
import { useT } from "../i18n";
import { EmptyState } from "../ui/states";
import type { Turn } from "./api";
import { UserMessage } from "./Bubbles";
import { Composer } from "./Composer";
import { timeline } from "./model";
import { TurnBlock } from "./TurnBlock";
import { useChatSender } from "./useChatSender";

export interface ChatPanelProps {
  conversationId: string;
  state: ConversationState;
  send: ConversationHandle["send"];
  turns: Turn[];
  selfManaged: boolean;
  emptyBody: string;
  /** Why a queued turn without a run waits: a task's running step, or the turn before. */
  waitHint: string;
  onCancel: (turnId: string) => void;
}

export function ChatPanel({ conversationId, state, send, turns, selfManaged, emptyBody, waitHint, onCancel }: ChatPanelProps) {
  const t = useT().chat;
  const sender = useChatSender(conversationId, state, turns, send, selfManaged);
  const view = timeline(state.events, turns);
  const byTurn = new Map(sender.pending.filter((p) => p.turnId).map((p) => [p.turnId!, p]));
  const known = new Set(turns.map((turn) => turn.id));
  // Prompts whose turn the list does not show yet (being created, refused, or not refetched).
  const loose = sender.pending.filter((p) => !p.turnId || !known.has(p.turnId));
  const queued = sender.pending.filter((p) => p.state === "creating" || p.state === "waiting" || p.state === "sending").length;
  const end = useRef<HTMLDivElement>(null);
  const count = state.events.length + sender.pending.length + turns.length;
  useEffect(() => {
    end.current?.scrollIntoView?.({ block: "end", behavior: "smooth" });
  }, [count]);
  const empty = view.before.length === 0 && view.turns.length === 0 && loose.length === 0;
  return (
    <div className="space-y-4">
      <div className="space-y-4" role="log" aria-label={t.agent} data-testid="chat-thread">
        {empty ? (
          <EmptyState compact icon={<ChatsCircle weight="duotone" />} title={t.empty}>
            {emptyBody}
          </EmptyState>
        ) : null}
        {foldEvents(view.before, (id) => state.segments.get(id)?.closedWith === undefined).map((item) =>
          item.kind === "thinking" ? (
            <ThinkingRow key={`thinking-${item.group.key}`} group={item.group} className="ms-11" />
          ) : (
            <EventRow key={`${item.event.segmentId}-${item.event.seq}-${item.event.direction}`} event={item.event} agent={t.agent} />
          ),
        )}
        {view.turns.map((entry) => {
          return <TurnBlock key={entry.turn.id} entry={entry} mine={byTurn.get(entry.turn.id)} waitHint={waitHint} onCancel={() => onCancel(entry.turn.id)} />;
        })}
        {loose.map((p) => (
          <UserMessage key={p.localId} text={p.text} delivery={p.state} error={p.error} />
        ))}
        <div ref={end} />
      </div>
      <p className="m-0 text-[12px] text-gray-500">{selfManaged ? t.history.self : t.history.managed}</p>
      <Composer onSend={(text, body) => void sender.submit(text, body)} queued={queued} />
    </div>
  );
}
